var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import './style.css';
import { FileOrigin, FileStatus } from 'filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import { useRef, useState } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import { useTranslation } from 'react-i18next';
import uploadImg from '@/assets/img/icons/upload.svg';
import { useResolveValue, useToast } from '@/hooks';
import { AWSService } from '@/services';
import { ValuesType } from '@/types';
import { formatFileSize, getInitialFiles, getInputBlockAnswer } from '@/utils';
import { formatMarkdownInline } from '@/utils/markdown';
import { Box } from '@chakra-ui/react';
import { BlockLayout } from '../fragments/BlockLayout';
registerPlugin(FilePondPluginFileValidateType, FilePondPluginFileValidateSize);
export const FileUploadMultiple = ({ block: { answer, cancel_label, completed_label, error_format_label, error_processing_label, formats, id, key, layout, max_files = null, max_files_exceeded_error_label, max_size, remove_label, required, uploading_label }, defaultValue = [], onChange, onNavigationLockChange, options }) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const fileUploadRef = useRef(null);
    const [fileList, setFileList] = useState([]);
    const [resolver] = useResolveValue();
    const resolved = {
        label: formatMarkdownInline(resolver.resolveString(answer.label)),
        labelButtonRemoveItem: formatMarkdownInline(resolver.resolveString(remove_label)),
        labelFileProcessing: resolver.resolveString(uploading_label),
        labelFileProcessingComplete: resolver.resolveString(completed_label),
        labelFileProcessingError: t('common:message.error.default'),
        labelFileTypeNotAllowed: resolver.resolveString(error_format_label),
        labelMaxFileSizeExceeded: resolver.resolveString(error_processing_label),
        labelMaxFilesExceeded: resolver.resolveString(max_files_exceeded_error_label),
        labelTapToCancel: formatMarkdownInline(resolver.resolveString(cancel_label)),
        required: resolver.resolveBoolean(required)
    };
    const labels = Object.assign(Object.assign({}, resolved), { labelIdle: `<img src='${uploadImg}'/> ${resolved.label}` });
    const handleChange = () => {
        var _a, _b;
        const currentFileList = (_b = (_a = fileUploadRef === null || fileUploadRef === void 0 ? void 0 : fileUploadRef.current) === null || _a === void 0 ? void 0 : _a.getFiles()) !== null && _b !== void 0 ? _b : [];
        const files = currentFileList
            .map((file) => ({
            format: file.fileType,
            id: file.serverId,
            name: file.filename,
            size: file.fileSize
        }))
            .filter((file) => file.id);
        const answerValue = Object.assign(Object.assign({}, answer), { label: resolved.label, value: files.length > 0 ? files : null });
        const newAnswer = getInputBlockAnswer(ValuesType.FILE_LIST, answerValue, key);
        onChange(newAnswer);
    };
    const handleSyncNavigationLock = () => {
        var _a, _b;
        const currentFileList = (_b = (_a = fileUploadRef === null || fileUploadRef === void 0 ? void 0 : fileUploadRef.current) === null || _a === void 0 ? void 0 : _a.getFiles()) !== null && _b !== void 0 ? _b : [];
        const isProcessing = currentFileList.some((file) => [FileStatus.PROCESSING, FileStatus.PROCESSING_QUEUED].includes(file.status));
        onNavigationLockChange === null || onNavigationLockChange === void 0 ? void 0 : onNavigationLockChange(isProcessing);
    };
    const handleInit = () => {
        // @ts-ignore - FilePond types are correct
        const files = getInitialFiles(defaultValue);
        setFileList(files);
        onNavigationLockChange === null || onNavigationLockChange === void 0 ? void 0 : onNavigationLockChange(false);
    };
    const handleUpdateFiles = (files) => {
        setFileList(files);
        handleSyncNavigationLock();
    };
    const handleProcessFile = (error, file) => {
        if (file.origin === FileOrigin.INPUT && !error) {
            handleChange();
        }
        handleSyncNavigationLock();
    };
    const handleAddFile = (_error, file) => {
        file.setMetadata('id', file.id);
    };
    const handleRemoveFile = () => {
        setTimeout(() => {
            handleChange();
        }, 200);
    };
    const handleWarning = (error) => {
        switch (error === null || error === void 0 ? void 0 : error.body) {
            case 'Max files':
                showToast({ message: resolved.labelMaxFilesExceeded });
                break;
            default:
                break;
        }
    };
    const handleServerProcess = (_fieldName, file, metadata, load, error, progress, abort) => __awaiter(void 0, void 0, void 0, function* () {
        const fileEl = document.getElementById(`filepond--item-${metadata.id}`);
        const fileAbortButton = fileEl.querySelector('.filepond--action-abort-item-processing');
        const actions = {
            abortFileProcessing: (action) => {
                fileAbortButton.addEventListener('click', action);
            }
        };
        yield AWSService.createS3File({
            awsFileIds: options,
            filepond: { abort, actions, error, file, load, progress }
        });
    });
    return (_jsx(BlockLayout, Object.assign({ layout: layout }, { children: _jsx(Box, Object.assign({ sx: {
                '&:focus-within .filepond--drop-label': {
                    border: '2px solid',
                    borderColor: 'darkBrandBlue.80',
                    borderRadius: 'var(--chakra-radii-md)'
                }
            } }, { children: _jsx(FilePond, Object.assign({ acceptedFileTypes: formats, allowMultiple: true, credits: false, "data-test": id, files: fileList, maxFileSize: formatFileSize(max_size), maxFiles: max_files, maxParallelUploads: 10, name: key, onaddfile: handleAddFile, oninit: handleInit, onprocessfile: handleProcessFile, onremovefile: handleRemoveFile, onupdatefiles: handleUpdateFiles, onwarning: handleWarning, ref: fileUploadRef, server: {
                    process: handleServerProcess,
                    remove: null,
                    revert: null
                } }, labels)) })) })));
};
